/**
 * What the iPad keeps for itself, in the browser's own database (IndexedDB).
 *
 * One small key and value store is all the POS needs: the catalog it sells from, the
 * operations it has not sent yet, its reference counter, and the sales it made today.
 * IndexedDB survives the tab being closed and the iPad being restarted, which the
 * browser's plain storage does not promise, and it holds far more.
 */

const DATABASE = "ohc-pos";
const STORE = "kv";

let opening: Promise<IDBDatabase> | undefined;

function database(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("The device storage could not be opened"));
  });
  return opening;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return database().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = work(transaction.objectStore(STORE));
        transaction.oncomplete = () => resolve(request.result);
        transaction.onerror = () => reject(transaction.error ?? new Error("The device storage failed"));
        transaction.onabort = () => reject(transaction.error ?? new Error("The device storage failed"));
      }),
  );
}

export async function read<T>(key: string): Promise<T | undefined> {
  return (await run("readonly", (store) => store.get(key))) as T | undefined;
}

export async function write(key: string, value: unknown): Promise<void> {
  await run("readwrite", (store) => store.put(value, key));
}

export async function remove(key: string): Promise<void> {
  await run("readwrite", (store) => store.delete(key));
}

/**
 * Change one value by a function, inside one transaction, so two changes made at the
 * same moment (a sale queued while a sync is finishing) cannot overwrite each other.
 */
export async function update<T>(key: string, change: (current: T | undefined) => T): Promise<T> {
  const db = await database();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, "readwrite");
    const store = transaction.objectStore(STORE);
    let next: T;
    const current = store.get(key);
    current.onsuccess = () => {
      next = change(current.result as T | undefined);
      store.put(next, key);
    };
    transaction.oncomplete = () => resolve(next);
    transaction.onerror = () => reject(transaction.error ?? new Error("The device storage failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("The device storage failed"));
  });
}
